import { Capacitor } from '@capacitor/core';

// Absolute production origins. The native app's WebView runs at https://localhost,
// so a relative URL (e.g. '/api', which the web build is often deployed with) would
// resolve to https://localhost/api and be intercepted in-app instead of hitting the
// backend — every API call (including login) then silently fails. On native we MUST
// use the absolute production URLs; on web we keep whatever the build is configured
// with (relative is fine there since it's same-origin).
const PROD_WEB_URL = 'https://app.legallumo.com';
const PROD_API_BASE_URL = `${PROD_WEB_URL}/api`;

const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};

// Return `configured` on web; on native, return it only if it's an absolute,
// non-loopback URL, otherwise fall back to the absolute production URL.
const nativeSafeUrl = (configured, prodFallback) => {
  if (!isNative()) return configured;
  const absolute = /^https?:\/\//i.test(configured);
  const loopback = /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(configured);
  return absolute && !loopback ? configured : prodFallback;
};

const config = {
  // API Configuration
  apiBaseURL: nativeSafeUrl(
    process.env.REACT_APP_API_BASE_URL || PROD_API_BASE_URL,
    PROD_API_BASE_URL
  ),

  baseWebURL: nativeSafeUrl(
    process.env.REACT_APP_BACKEND_URL || PROD_WEB_URL,
    PROD_WEB_URL
  ),


  // App Configuration
  appName: process.env.REACT_APP_APP_NAME || 'Trademark Search Platform',
  appNameShort: process.env.REACT_APP_APP_NAME_SHORT || 'TMK Search',
  version: process.env.REACT_APP_VERSION || '1.0.0',
  environment: process.env.REACT_APP_ENVIRONMENT || 'development',
  debug: process.env.REACT_APP_DEBUG === 'true',

  // Build Configuration
  publicPath: process.env.REACT_APP_PUBLIC_PATH || '/',

  // Company Information
  companyName: process.env.REACT_APP_COMPANY_NAME || 'Your Company',
  supportEmail: process.env.REACT_APP_SUPPORT_EMAIL || 'support@example.com',

  // Feature Flags
  features: {
    multiLanguage: true,
    darkMode: true,
    fileUpload: true,
    searchHistory: true,
    reports: true,
    userManagement: true,
    branding: true,
  },

  // UI Configuration
  ui: {
    itemsPerPage: 20,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    allowedImageTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    searchDebounceMs: 300,
    toastDuration: 5000,
  },

  // Authentication
  auth: {
    tokenKey: 'authToken',
    userKey: 'user',
    rememberMeKey: 'rememberMe',
    sessionTimeout: 60 * 60 * 1000, // 30 minutes
  },

  // API Endpoints
  endpoints: {
    auth: {
      clientLogin: '/auth/client/login',
      clientUserLogin: '/auth/client-user/login',
      logout: '/auth/logout',
      verify: '/auth/verify',
      refresh: '/auth/refresh',
    },
    client: {
      profile: '/client/profile',
      license: '/client/license',
      users: '/client/users',
      password: '/client/password',
    },
    clientUser: {
      profile: '/client-user/profile',
      quota: '/client-user/quota',
      parentClient: '/client-user/parent-client',
    },
    trademark: {
      search: '/trademark/search',
      history: '/trademark/history',
      quota: '/trademark/quota',
      dataModels: '/trademark/data-models',
      health: '/trademark/health',
    },
    user: {
      preferences: '/user/preferences',
      notes: '/user/notes',
      reports: '/user/reports',
      documents: '/user/documents',
      dashboard: '/user/dashboard',
    },
    search: {
      history: '/search/history',
      statistics: '/search/statistics',
      topSearches: '/search/top-searches',
      quotaUsage: '/search/quota/usage',
      quotaStatus: '/search/quota/status',
    },
    files: {
      client: {
        profiles: '/files/client/profiles',
        logos: '/files/client/logos',
      },
      secure: '/files/secure',
    },
    system: {
      health: '/system/health',
    },
  },
};

export default config;